import React, { useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { trackEvent } from "@/lib/analytics";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const WELCOME = "Hi! I'm the Auto Basic Care assistant. Ask me about services, hours, or say 'I need an oil change Friday' and I'll get you booked.";

const QUICK_PROMPTS = [
  "What hours are you open?",
  "I need an oil change",
  "Do you sell tires?",
  "Do you offer fleet maintenance?",
];

function newSessionId() {
  try {
    let id = localStorage.getItem("abc_chat_sid");
    if (!id) {
      id = "s_" + Math.random().toString(36).slice(2) + Date.now().toString(36);
      localStorage.setItem("abc_chat_sid", id);
    }
    return id;
  } catch {
    return "anon_" + Date.now();
  }
}

const ESC = { "&": "&amp;", "<": "&lt;", ">": "&gt;", '"': "&quot;", "'": "&#39;" };
function mdFormat(str = "") {
  const safe = String(str).replace(/[&<>"']/g, (c) => ESC[c]);
  return safe
    .replace(/\*\*(.+?)\*\*/g, "<strong>$1</strong>")
    .replace(/(^|[\s(])\*([^*\s][^*]*?)\*/g, "$1<em>$2</em>")
    .replace(/\n/g, "<br/>");
}

export default function ChatWidget() {
  const [open, setOpen] = useState(false);
  const [unread, setUnread] = useState(true);
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [sending, setSending] = useState(false);
  const sessionId = useMemo(newSessionId, []);
  const scrollerRef = useRef(null);

  useEffect(() => {
    if (scrollerRef.current) {
      scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
    }
  }, [messages, open]);

  const send = async (text) => {
    const msg = (text ?? input).trim();
    if (!msg || sending) return;
    setInput("");
    setMessages((m) => [...m, { role: "user", text: msg }]);
    setSending(true);
    try {
      const { data } = await axios.post(`${API}/chat`, { session_id: sessionId, message: msg });
      setMessages((m) => [...m, { role: "assistant", text: data.reply || "...", action: data.action }]);
      if (data.action && data.action.type === "book") {
        window.dispatchEvent(new CustomEvent("abc:book", { detail: data.action }));
      }
    } catch (e) {
      setMessages((m) => [...m, { role: "assistant", text: "Sorry, I'm having trouble responding. Please call 205-405-1238." }]);
    } finally {
      setSending(false);
    }
  };

  const onKey = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      send();
    }
  };

  const toggle = () => {
    setOpen((o) => {
      const next = !o;
      if (next) trackEvent("Contact", { source: "chat" });
      return next;
    });
    setUnread(false);
  };

  return (
    <>
      <button
        className={`abc-chat-fab ${open ? "open" : ""}`}
        onClick={toggle}
        aria-label={open ? "Close chat" : "Open chat with Auto Basic Care assistant"}
        data-testid="chat-fab"
      >
        {open ? (
          <svg width="22" height="22" viewBox="0 0 24 24" fill="none" aria-hidden="true">
            <path d="M6 6L18 18M18 6L6 18" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" />
          </svg>
        ) : (
          <>
            <svg width="24" height="24" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M21 11.5a8.38 8.38 0 0 1-.9 3.8 8.5 8.5 0 0 1-7.6 4.7 8.38 8.38 0 0 1-3.8-.9L3 21l1.9-5.7a8.38 8.38 0 0 1-.9-3.8 8.5 8.5 0 0 1 4.7-7.6 8.38 8.38 0 0 1 3.8-.9h.5a8.48 8.48 0 0 1 8 8v.5z" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
            <span className="abc-chat-fab-label">Chat</span>
            {unread && <span className="abc-chat-dot" />}
          </>
        )}
      </button>

      <div className={`abc-chat-panel ${open ? "open" : ""}`} role="dialog" aria-label="Chat assistant" data-testid="chat-panel">
        <div className="abc-chat-head">
          <div>
            <strong>Auto Basic Care</strong>
            <small>Virtual assistant · usually replies in seconds</small>
          </div>
          <button className="abc-chat-close" onClick={toggle} aria-label="Close" data-testid="chat-close">×</button>
        </div>

        <div className="abc-chat-body" ref={scrollerRef}>
          {messages.map((m, i) => (
            <div key={i} className={`abc-chat-msg ${m.role}`}>
              <div className="abc-chat-bubble" dangerouslySetInnerHTML={{ __html: mdFormat(m.text) }} />
              {m.action && m.action.service && (
                <button
                  className="abc-chat-action"
                  onClick={() => window.dispatchEvent(new CustomEvent("abc:book", { detail: m.action }))}
                  data-testid="chat-book-action"
                >
                  Book {m.action.service}{m.action.day ? ` · ${m.action.day}` : ""}{m.action.time ? ` · ${m.action.time}` : ""} →
                </button>
              )}
            </div>
          ))}
          {sending && (
            <div className="abc-chat-msg assistant">
              <div className="abc-chat-bubble abc-chat-typing"><span /><span /><span /></div>
            </div>
          )}

          {messages.length <= 1 && !sending && (
            <div className="abc-chat-quick">
              {QUICK_PROMPTS.map((p) => (
                <button key={p} onClick={() => send(p)} data-testid={`chat-quick-${p.replace(/[^a-z0-9]+/gi,'-').toLowerCase()}`}>
                  {p}
                </button>
              ))}
            </div>
          )}
        </div>

        <form
          className="abc-chat-input-row"
          onSubmit={(e) => { e.preventDefault(); send(); }}
        >
          <input
            type="text"
            placeholder="Type a message…"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={onKey}
            disabled={sending}
            data-testid="chat-input"
            autoComplete="off"
          />
          <button type="submit" disabled={sending || !input.trim()} aria-label="Send" data-testid="chat-send">
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" aria-hidden="true">
              <path d="M22 2L11 13M22 2L15 22L11 13M22 2L2 9L11 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </button>
        </form>
      </div>
    </>
  );
}
